/**
 * Autosave: the open level saves on its own a moment after edits stop, like
 * Google Docs or Figma, rather than on request as Unreal and Unity do. This is
 * a website, and a visitor shouldn't lose a remix to a closed tab.
 *
 * It only decides *when*: any world write (re)starts a short wait, a level
 * load cancels it, and `flush()` saves at once (the page is being hidden).
 * *What* is saved, and where, is `level.save` (LevelSaves).
 */
import type { Unsubscribe } from "../../core/Emitter";
import type { CancelTimer, Timers } from "../../core/Timers";
import type { WorldReader } from "../../world/World";

/** How long after the last edit the level saves: long enough that typing doesn't save per keystroke. */
export const AUTOSAVE_DELAY_MS = 1000;

export interface AutosaveDeps {
  readonly world: WorldReader;
  /** Saves the open level (dispatches `level.save`). */
  readonly save: () => void;
  readonly timers: Timers;
}

export class Autosave {
  private pending: CancelTimer | null = null;
  private readonly unsubscribe: Unsubscribe[];

  constructor(private readonly deps: AutosaveDeps) {
    const edited = () => {
      this.schedule();
    };
    this.unsubscribe = [
      deps.world.events.on("entity:changed", edited),
      deps.world.events.on("component:changed", edited),
      // A new level (or a restored save) starts with nothing to save.
      deps.world.events.on("world:replaced", () => {
        this.cancel();
      }),
    ];
  }

  /** Saves a pending edit now, if there is one. */
  flush(): void {
    if (!this.pending) return;
    this.cancel();
    this.deps.save();
  }

  dispose(): void {
    this.cancel();
    this.unsubscribe.forEach((off) => {
      off();
    });
  }

  private schedule(): void {
    this.cancel();
    this.pending = this.deps.timers.after(AUTOSAVE_DELAY_MS, () => {
      this.pending = null;
      this.deps.save();
    });
  }

  private cancel(): void {
    this.pending?.();
    this.pending = null;
  }
}
